import { Modal } from "antd";
import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import {
    composeValidators,
    emailValidator,
    minLengthValidator,
    requiredValidator,
} from "components/Form/Form.utils";
import { EgressEmailWarning } from "components/EgressMailWarning";
import { StoreProviderForm } from "components/Form/StoreProviderForm";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { Form } from "components/Form/components/Form/Form";
import { SubmitLock } from "components/Form/components/SubmitLock/SubmitLock";
import { PasswordFormComponent } from "components/Form/components/Password/PasswordFormComponent";
import { TextFormComponent } from "components/Form/components/Text/TextFormComponent";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import SimpleNotification from "components/Notification/SimpleNotification";
import { TimeLeft } from "components/TimeAgo/TimeAgo";
import { TimeAgoWithDateTimeFormatter } from "components/TimeAgo/TimeAgoWithDateTimeFormatter";
import { DocsGenericTooltip } from "components/Tooltip/DocsInfoTooltip";
import { ResourceConfig } from "config/config";
import { get2FAProvisioningUri, getRandomOtpSecret } from "config/utils";
import {
    changeEmailPath,
    changePasswordPath,
    deleteUserPath,
    disableTwoFaPath,
    enableTwoFaPath,
    resendEmailPath,
} from "containers/Auth/constants";
import {
    AuthMethodsContainer,
    defaultAuthValue,
} from "containers/Auth/screens/AuthMethodsContainer/AuthMethodsContainer";
import { userColumns } from "containers/Auth/screens/IdentityManagement/columns";
import { TwoFaCodeField, TwoFaCodeFieldIfEnabled } from "containers/Auth/sharedFields";
import { Action, ActionData } from "containers/Resources/components/CommonActions/Action";
import { createModalActionSettlement } from "containers/Resources/components/CommonActions/actionModalSettlement";
import {
    exportActionSignature,
    forceRecordUpdate,
    isDeleted,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
    referencesUsedByTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { buildSimpleColumn } from "containers/Search/components/SearchTabs/columns";
import { User } from "graphql/generated/graphql";
import QRCode from "qrcode.react";
import { Field, Form as FinalForm, FormSpy } from "react-final-form";
import { signOutPath } from "utils/RouterManager/constants";
import { mapScreensToPaths } from "utils/RouterManager/utils";
import identity from "./identity";
import resource from "./resource";
import "./user.scss";

const EnableTwofaAction: Action = (data, dispatch, history) => {
    const modal = Modal.confirm({
        title: "Enable two-factor authentication",
        icon: null,
        onOk: () => {},
        onCancel: () => {},
        okText: "",
        cancelText: "",
        okButtonProps: { disabled: true },
        cancelButtonProps: { disabled: true },
        className: "user-auth-modal",
    });

    return new Promise((resolve, reject) => {
        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });
        const randomOtpSecret = getRandomOtpSecret();
        const provisioningUri = get2FAProvisioningUri(randomOtpSecret, data.resourceData.email);

        const onSubmit = (formData: any) => {
            return restClient
                .post(enableTwoFaPath, {
                    user_id: data.resourceData.id,
                    password: formData.password,
                    new_twofa_code: formData.twofaCode,
                    new_otp_secret: randomOtpSecret,
                    authentication_method: formData.authenticationMethod ?? defaultAuthValue,
                })
                .then((response) => {
                    resolve(response);
                    NotificationService.success(
                        `2FA has been enabled for ${data.resourceData.name}`
                    );
                    history.push(signOutPath);
                })
                .catch((error) => {
                    reject(error);
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride="There was a problem enabling 2FA"
                            error={error}
                        />
                    );
                })
                .finally(() => {
                    modal.destroy();
                });
        };

        const form = (
            <StoreProviderForm>
                <FinalForm onSubmit={onSubmit}>
                    {({ handleSubmit, submitting }) => (
                        <Form onSubmit={handleSubmit}>
                            <SubmitLock locked={submitting}>
                                <Form.Row>
                                    <p className="user-auth-modal__note">
                                        Please scan the QR code with your two-factor authentication app
                                        (eg. Google Authenticator or Authy):
                                    </p>
                                    <QRCode value={provisioningUri} size={300} />
                                </Form.Row>
                                <AuthMethodsContainer />
                                <Form.Row>
                                    <p className="user-auth-modal__note">
                                        Please enter your current password.
                                    </p>
                                    <Field
                                        name="password"
                                        id="password"
                                        fieldProps={{
                                            label: "Password",
                                            clearable: false,
                                        }}
                                        component={PasswordFormComponent}
                                        validate={requiredValidator}
                                    />
                                </Form.Row>
                                <Form.Row>
                                    <p className="user-auth-modal__note">
                                        Please enter a current 2FA code.
                                    </p>
                                    <TwoFaCodeField isRequired />
                                </Form.Row>
                                <Form.Row className="sign-in--offset">
                                    <CancelButton onClick={settlement.cancel} disabled={submitting} />
                                    <Button variation="primary" submit loading={submitting} label="OK" />
                                </Form.Row>
                            </SubmitLock>
                        </Form>
                    )}
                </FinalForm>
            </StoreProviderForm>
        );

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: form,
        }));
    });
};

const DisableTwofaAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });

        const isOwnUser = data?.userData?.identity_id === data?.resourceData?.id;

        const modal = Modal.confirm({
            title: "Disable two-factor authentication",
            icon: null,
            onOk: () => {},
            onCancel: () => {},
            okText: "",
            cancelText: "",
            okButtonProps: { disabled: true },
            cancelButtonProps: { disabled: true },
            className: "user-auth-modal",
        });

        const onSubmit = (formData: any) => {
            return restClient
                .post(disableTwoFaPath, {
                    user_id: data.resourceData.id,
                    password: formData.password,
                    twofa_code: formData.twofaCode,
                    authentication_method: formData.authenticationMethod ?? defaultAuthValue,
                })
                .then((response: any) => {
                    resolve(response);
                    NotificationService.success(
                        `2FA has been disabled for ${data.resourceData.name}`
                    );
                    if (isOwnUser) {
                        history.push(signOutPath);
                    } else {
                        forceRecordUpdate(dispatch);
                    }
                })
                .catch((error) => {
                    reject(error);
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride="There was a problem disabling 2FA"
                            error={error}
                        />
                    );
                })
                .finally(() => {
                    modal.destroy();
                });
        };

        const is2FACodeRequired = isOwnUser || data?.userData?.is_twofa_enabled;
        const form = (
            <StoreProviderForm>
                <FinalForm onSubmit={onSubmit}>
                    {({ handleSubmit, submitting }) => (
                        <Form onSubmit={handleSubmit}>
                            <SubmitLock locked={submitting}>
                                <AuthMethodsContainer />
                                <Form.Row>
                                    <p className="user-auth-modal__note">
                                        Please enter your current password.
                                    </p>
                                    <Field
                                        name="password"
                                        id="password"
                                        fieldProps={{
                                            label: "Password",
                                            clearable: false,
                                        }}
                                        component={PasswordFormComponent}
                                        validate={requiredValidator}
                                    />
                                </Form.Row>
                                <Form.Row>
                                    <p className="user-auth-modal__note">
                                        Please enter a current 2FA code.
                                    </p>
                                    <TwoFaCodeField isRequired={is2FACodeRequired} />
                                </Form.Row>
                                <Form.Row className="sign-in--offset">
                                    <CancelButton onClick={settlement.cancel} disabled={submitting} />
                                    <Button variation="primary" submit loading={submitting} label="OK" />
                                </Form.Row>
                            </SubmitLock>
                        </Form>
                    )}
                </FinalForm>
            </StoreProviderForm>
        );

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: form,
        }));
    });
};

const ReInviteAction: Action = (data, dispatch, history) => {
    let lastNotificationId: any;

    return new Promise((resolve, reject) => {
        const modal = Modal.confirm({
            title: "Resend email to invited user",
            icon: null,
            onOk: () => {},
            onCancel: () => {},
            okText: "",
            cancelText: "",
            okButtonProps: { disabled: true },
            cancelButtonProps: { disabled: true },
            className: "user-auth-modal",
        });

        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });

        const onSubmit = (formData: any) => {
            NotificationService.dismiss(lastNotificationId);
            return restClient
                .post(resendEmailPath, {
                    user_id: data.userData.identity_id,
                    password: formData.password,
                    twofa_code: formData.twofaCode,
                    invited_user_id: data.resourceData.id,
                    authentication_method: formData.authenticationMethod ?? defaultAuthValue,
                })
                .then((response) => {
                    resolve(response);
                    lastNotificationId = NotificationService.success(
                        `Activation email was sent to ${data.resourceData?.pending_email}`
                    );
                })
                .catch((error) => {
                    reject(error);
                    lastNotificationId = NotificationService.error(
                        <RestErrorNotification
                            titleOverride="There was a problem resending email"
                            error={error}
                        />
                    );
                })
                .finally(() => {
                    modal.destroy();
                });
        };

        const form = (
            <StoreProviderForm>
                <FinalForm onSubmit={onSubmit}>
                    {({ handleSubmit, submitting }) => (
                        <Form onSubmit={handleSubmit}>
                            <SubmitLock locked={submitting}>
                                <AuthMethodsContainer />
                                <Form.Row>
                                    <p className="user-auth-modal__note">
                                        Please enter your current password.
                                    </p>
                                    <Field
                                        name="password"
                                        id="password"
                                        fieldProps={{
                                            label: "Password",
                                            clearable: false,
                                        }}
                                        component={PasswordFormComponent}
                                        validate={requiredValidator}
                                    />
                                </Form.Row>
                                <TwoFaCodeFieldIfEnabled />
                                <Form.Row className="sign-in--offset">
                                    <CancelButton onClick={settlement.cancel} disabled={submitting} />
                                    <Button variation="primary" submit loading={submitting} label="OK" />
                                </Form.Row>
                            </SubmitLock>
                        </Form>
                    )}
                </FinalForm>
            </StoreProviderForm>
        );

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: form,
        }));
    });
};

const ChangeEmailAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });
        let lastNotificationId: any;

        const modal = Modal.confirm({
            title: "Change email address",
            icon: null,
            onOk: () => {},
            onCancel: () => {},
            okText: "",
            cancelText: "",
            okButtonProps: { disabled: true },
            cancelButtonProps: { disabled: true },
            className: "user-auth-modal",
        });

        const onSubmit = (formData: any) => {
            NotificationService.dismiss(lastNotificationId);
            return restClient
                .post(changeEmailPath, {
                    user_id: data.resourceData.id,
                    password: formData.password,
                    twofa_code: formData.twofaCode,
                    new_pending_email: formData.newEmailAddress,
                    authentication_method: formData.authenticationMethod ?? defaultAuthValue,
                })
                .then((response) => {
                    resolve(response);
                    lastNotificationId = NotificationService.success(
                        <SimpleNotification
                            title="Email change requested"
                            subtitle="Please check your inbox"
                        />
                    );
                    history.push(signOutPath);
                })
                .catch((error) => {
                    reject(error);
                    lastNotificationId = NotificationService.error(
                        <RestErrorNotification
                            titleOverride="There was a problem changing your email"
                            error={error}
                        />
                    );
                })
                .finally(() => {
                    modal.destroy();
                });
        };

        const form = (
            <StoreProviderForm>
                <FinalForm
                    onSubmit={onSubmit}
                    initialValues={{ newEmailAddress: data.resourceData?.email }}
                >
                    {({ handleSubmit, submitting }) => (
                        <Form onSubmit={handleSubmit}>
                            <SubmitLock locked={submitting}>
                                <Form.Row>
                                    <p className="user-auth-modal__note">
                                        Please enter your new email address. We will send an email
                                        containing a link to confirm the change.
                                    </p>
                                    <Field
                                        name="newEmailAddress"
                                        id="newEmailAddress"
                                        fieldProps={{
                                            label: "New email address",
                                        }}
                                        component={TextFormComponent}
                                        validate={composeValidators(requiredValidator, emailValidator)}
                                    />
                                </Form.Row>
                                <FormSpy subscription={{ values: true }}>
                                    {({ values }) =>
                                        values?.newEmailAddress !== data.resourceData?.email ? (
                                            <EgressEmailWarning
                                                email={values?.newEmailAddress}
                                                consequence="You will not receive the confirmation email, so the change cannot be completed."
                                            />
                                        ) : null
                                    }
                                </FormSpy>
                                <AuthMethodsContainer />
                                <Form.Row>
                                    <p className="user-auth-modal__note">
                                        Please enter your current password.
                                    </p>
                                    <Field
                                        name="password"
                                        id="password"
                                        fieldProps={{
                                            label: "Password",
                                            clearable: false,
                                        }}
                                        component={PasswordFormComponent}
                                        validate={requiredValidator}
                                    />
                                </Form.Row>
                                <TwoFaCodeFieldIfEnabled />
                                <Form.Row className="sign-in--offset">
                                    <CancelButton onClick={settlement.cancel} disabled={submitting} />
                                    <Button variation="primary" submit loading={submitting} label="Change email address" />
                                </Form.Row>
                            </SubmitLock>
                        </Form>
                    )}
                </FinalForm>
            </StoreProviderForm>
        );

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: form,
        }));
    });
};

const ChangePasswordAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });
        let lastNotificationId: any;

        const modal = Modal.confirm({
            title: "Change password",
            icon: null,
            onOk: () => {},
            onCancel: () => {},
            okText: "",
            cancelText: "",
            okButtonProps: { disabled: true },
            cancelButtonProps: { disabled: true },
            className: "user-auth-modal",
        });

        const onSubmit = (formData: any) => {
            NotificationService.dismiss(lastNotificationId);
            return restClient
                .post(changePasswordPath, {
                    user_id: data.resourceData.id,
                    password: formData.password,
                    twofa_code: formData.twofaCode,
                    new_password: formData.newPassword,
                    authentication_method: formData.authenticationMethod ?? defaultAuthValue,
                })
                .then((response) => {
                    resolve(response);
                    lastNotificationId = NotificationService.success("Password changed");
                    history.push(signOutPath);
                })
                .catch((error) => {
                    reject(error);
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride="There was a problem changing your password"
                            error={error}
                        />
                    );
                })
                .finally(() => {
                    modal.destroy();
                });
        };

        const form = (
            <StoreProviderForm>
                <FinalForm onSubmit={onSubmit}>
                    {({ handleSubmit, submitting }) => (
                        <Form onSubmit={handleSubmit}>
                            <SubmitLock locked={submitting}>
                                <Form.Row>
                                    <p className="user-auth-modal__note">
                                        Please choose a new password.
                                    </p>
                                    <Field
                                        name="newPassword"
                                        id="newPassword"
                                        fieldProps={{
                                            label: "New password",
                                        }}
                                        component={PasswordFormComponent}
                                        validate={composeValidators(
                                            requiredValidator,
                                            minLengthValidator(8)
                                        )}
                                    />
                                </Form.Row>
                                <AuthMethodsContainer />
                                <Form.Row>
                                    <p className="user-auth-modal__note">
                                        Please enter your current password.
                                    </p>
                                    <Field
                                        name="password"
                                        id="password"
                                        fieldProps={{
                                            label: "Password",
                                            clearable: false,
                                        }}
                                        component={PasswordFormComponent}
                                        validate={requiredValidator}
                                    />
                                </Form.Row>
                                <TwoFaCodeFieldIfEnabled />
                                <Form.Row className="sign-in--offset">
                                    <CancelButton onClick={settlement.cancel} disabled={submitting} />
                                    <Button variation="primary" submit loading={submitting} label="Change password" />
                                </Form.Row>
                            </SubmitLock>
                        </Form>
                    )}
                </FinalForm>
            </StoreProviderForm>
        );

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: form,
        }));
    });
};

const DeleteUserAction: Action = (data, dispatch, history) => {
    let lastNotificationId: any;

    const title = (
        <>
            <div>Delete user</div>
            <div className="user-delete-permanently__title">
                <Icon icon={IconCollection.alertCircleOutline} />
                <span>Please note that the user will be deleted permanently</span>
            </div>
        </>
    );

    return new Promise((resolve, reject) => {
        const modal = Modal.confirm({
            title: title,
            icon: null,
            onOk: () => {},
            onCancel: () => {},
            okText: "",
            cancelText: "",
            okButtonProps: { disabled: true },
            cancelButtonProps: { disabled: true },
            className: "user-auth-modal",
        });

        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });

        const onSubmit = (formData: any) => {
            NotificationService.dismiss(lastNotificationId);
            return restClient
                .post(deleteUserPath, {
                    user_id: data.userData.identity_id,
                    password: formData.password,
                    twofa_code: formData.twofaCode,
                    delete_user_id: data.resourceData.id,
                    authentication_method: formData.authenticationMethod ?? defaultAuthValue,
                })
                .then((response) => {
                    resolve(response);
                    lastNotificationId = NotificationService.success("The user was deleted");
                    if (history.location.pathname === "/workspace") {
                        return;
                    } else if (history.location.state === undefined) {
                        history.push(mapScreensToPaths.userRoleManagement);
                    } else {
                        history.goBack();
                    }
                })
                .catch((error) => {
                    reject(error);
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride="The user cannot be deleted"
                            error={error}
                        />
                    );
                })
                .finally(() => {
                    modal.destroy();
                });
        };

        const form = (
            <StoreProviderForm>
                <FinalForm onSubmit={onSubmit}>
                    {({ handleSubmit, submitting }) => (
                        <Form onSubmit={handleSubmit}>
                            <SubmitLock locked={submitting}>
                                <AuthMethodsContainer />
                                <Form.Row>
                                    <p className="user-auth-modal__note">
                                        Please enter your current password.
                                    </p>
                                    <Field
                                        name="password"
                                        id="password"
                                        fieldProps={{
                                            label: "Password",
                                            clearable: false,
                                        }}
                                        component={PasswordFormComponent}
                                        validate={requiredValidator}
                                    />
                                </Form.Row>
                                <TwoFaCodeFieldIfEnabled />
                                <Form.Row className="sign-in--offset">
                                    <CancelButton onClick={settlement.cancel} disabled={submitting} />
                                    <Button variation="primary" submit loading={submitting} label="OK" />
                                </Form.Row>
                            </SubmitLock>
                        </Form>
                    )}
                </FinalForm>
            </StoreProviderForm>
        );

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: form,
        }));
    });
};

const ClearLocalStorageAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        localStorage.clear();
        resolve(true);
    }).then(() => {
        NotificationService.success("Local settings cleared");
    });
};

const typedWindow: any = window;

export const deleteUserKey = "deleteUser";
export const enableTwoFAKey = "enable2FA";
export const disableTwoFAKey = "disable2FA";
export const changePasswordKey = "changePassword";

const resourceName = "user";

const config: ResourceConfig = {
    label: "User",
    icon: IconCollection.accountOutline,
    resourceName,
    resourceClass: "resource",
    hideCreateNew: true,
    ignoreIsReadonly: true,
    isBulkDeleteForbidden: true,
    defaultActiveTab: logsTabKey,
    layout: [
        {
            partial: "pageHeaderForm",
            config: {
                editIcon: true,
            },
        },
        { partial: "form" },
        { partial: "userGitPat" },
        { partial: "userApiKey" },
        { partial: "userConnectedApps" },
        { partial: "userGitPublicKeyList" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, commentsTab.tab, referencesUsedByTab.tab, metadataTab.tab],
                content: [logsTab.content, commentsTab.content, referencesUsedByTab.content, metadataTab.content],
            },
        },
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            ${identity.graphql.resourceBody}
            icon
            email
            pending_email
            is_activated
            is_enabled
            is_twofa_enabled
            has_pat
            git_pat_created_at
            git_pat_last_used_at
            last_activity
            user_git_public_key_plain_list_user_id(limit: 100, order: "+id") {
                id
                public_key
            }
            is_engine_user
            is_ssp_user
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            icon
            email
            pending_email
            is_activated
            is_enabled
            is_twofa_enabled
            last_activity
            activation_deadline
            is_locked_out
            is_engine_user
            is_ssp_user
            last_login
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $icon: String, 
            $is_enabled: Boolean,
            $is_engine_user: Boolean,
            $is_ssp_user: Boolean,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            icon: $icon, 
            is_enabled: $is_enabled,
            is_engine_user: $is_engine_user,
            is_ssp_user: $is_ssp_user,
        `,
    },
    additionalColumns: (setNewValue) => [
        {
            key: "activation_deadline",
            label: "Activation deadline",
            shown: false,
            sortable: false,
            dto: (value) =>
                value.activation_deadline
                    ? buildSimpleColumn(
                          <TimeLeft datetime={value.activation_deadline} />,
                          setNewValue
                              ? () =>
                                    setNewValue({
                                        field: "activation_deadline",
                                        value: value.activation_deadline,
                                    })
                              : undefined
                      )
                    : null,
        },
        ...userColumns(setNewValue),
        {
            label: "Pending email",
            key: "pending_email",
            shown: true,
            sortable: true,
            dto: ({ pending_email }: User) =>
                pending_email
                    ? buildSimpleColumn(
                          pending_email,
                          setNewValue
                              ? () =>
                                    setNewValue({
                                        field: "pending_email",
                                        value: pending_email,
                                    })
                              : undefined
                      )
                    : null,
            filterOptions: {
                preset: "nullable",
            },
        },
        {
            label: "Is engine user",
            key: "is_engine_user",
            shown: true,
            sortable: true,
            dto: ({ is_engine_user }: User) =>
                is_engine_user
                    ? buildSimpleColumn(
                          <Icon icon={IconCollection.check} />,
                          setNewValue
                              ? () => setNewValue({ field: "is_engine_user", value: true })
                              : undefined
                      )
                    : null,
            filterOptions: {
                preset: "ternary",
            },
        },
        {
            label: "Is ssp user",
            key: "is_ssp_user",
            shown: true,
            sortable: true,
            dto: ({ is_ssp_user }: User) =>
                is_ssp_user
                    ? buildSimpleColumn(
                          <Icon icon={IconCollection.check} />,
                          setNewValue
                              ? () => setNewValue({ field: "is_ssp_user", value: true })
                              : undefined
                      )
                    : null,
            filterOptions: {
                preset: "ternary",
            },
        },
    ],
    actions: {
        shown: [
            {
                key: enableTwoFAKey,
                action: EnableTwofaAction,
                icon: () => IconCollection.shieldKeyOutline,
                label: () => "Enable two-factor authentication",
                omitWhen: (data: ActionData) =>
                    data.resourceData.id !== data.userData.identity_id || // cannot enable for other user
                    data.resourceData?.is_twofa_enabled, // cannot enable when already enabled
            },
            {
                key: disableTwoFAKey,
                action: DisableTwofaAction,
                icon: () => IconCollection.shieldKeyOutline,
                label: () => "Disable two-factor authentication",
                omitWhen: (data: ActionData) => {
                    return (
                        (data.resourceData.id === data.userData.identity_id // if own user has 2FA not enabled
                            ? !data.resourceData?.is_twofa_enabled
                            : !data?.canUserCreateUser || !data.resourceData?.is_twofa_enabled) || // if user has no CREATE permission or 2FA is not enabled for other user
                        typedWindow?.REACT_APP_REQUIRE_SECOND_FACTOR // cannot disable when required
                    );
                },
            },
            {
                key: "reInvite",
                action: ReInviteAction,
                icon: () => IconCollection.shieldKeyOutline,
                label: () => "Resend email to invited user",
                disableWhen: isDeleted,
                omitWhen: (data: ActionData) =>
                    data.resourceData?.id === data.userData.identity_id || // cannot resend for own user
                    data.resourceData?.is_activated || // cannot resend for activated user
                    !data.resourceData?.pending_email, // cannot resend without pending_email
            },
            {
                key: "changeEmail",
                action: ChangeEmailAction,
                icon: () => IconCollection.shieldKeyOutline,
                label: () => "Change email address",
                omitWhen: (data: ActionData) => data.resourceData.id !== data.userData.identity_id,
            },
            {
                key: changePasswordKey,
                action: ChangePasswordAction,
                icon: () => IconCollection.shieldKeyOutline,
                label: () => "Change password",
                omitWhen: (data: ActionData) => data.resourceData.id !== data.userData.identity_id,
            },
            {
                key: deleteUserKey,
                action: DeleteUserAction,
                icon: () => IconCollection.deleteOutline,
                label: () => "Delete user",
                disableWhen: isDeleted,
                omitWhen: (data: ActionData) => {
                    const isBootstrapUser = data.bootstrapUserName === data.resourceData?.name;
                    const isOwnUser = data.resourceData?.id === data.userData.identity_id;

                    return isOwnUser || isBootstrapUser;
                },
            },
            {
                key: "clearLocalStorageKey",
                action: ClearLocalStorageAction,
                icon: () => IconCollection.deleteOutline,
                label: () => "Clear local settings",
                omitWhen: (data: ActionData) => data.resourceData?.id !== data.userData.identity_id,
                tooltipComponent: ({ children }) => (
                    <DocsGenericTooltip title="Clear local settings" urlPath="/docs">
                        {children}
                    </DocsGenericTooltip>
                ),
            },
        ],
        hidden: [exportActionSignature],
    },
    listActions: {
        shown: [],
        hidden: [],
    },
    form: [
        {
            name: "email",
            component: "value",
            fieldProps: {
                label: "Email",
            },
        },
        {
            name: "is_enabled",
            component: "switch",
            fieldProps: {
                label: "Is enabled",
            },
        },
        {
            name: "is_twofa_enabled",
            component: "value",
            fieldProps: {
                label: "2FA enabled",
            },
        },
        {
            name: "is_engine_user",
            component: "switch",
            fieldProps: {
                label: "Access to Cloudomation Engine UI",
            },
        },
        {
            name: "is_ssp_user",
            component: "switch",
            fieldProps: {
                label: "Access to Cloudomation DevStack portal",
                checkAccess: true,
            },
        },
    ],
    detailsSidebar: [
        {
            label: "Last activity",
            valueKey: "last_activity",
            dto: (value: any) => (value ? <TimeAgoWithDateTimeFormatter datetime={value} /> : null),
        },
        {
            label: "Pending email",
            valueKey: "pending_email",
            dto: (value: any) => value,
        },
    ],
};
export default config;
